/**
 * Template Workflow Timeline Component
 * Displays all template workflow steps in a vertical timeline layout
 */

import { useEffect } from 'react';
import { WorkflowStep } from './WorkflowStep';
import { TemplateWorkflowState, TemplateWorkflowStepId } from '@/types/workflow';
import { useTemplateWorkflowStore } from '@/stores/templateWorkflowStore';
import {
  startValidation,
  startLayoutGeneration,
  startDesignDocument,
  viewValidation,
  viewLayouts,
  viewDesignDocument,
  rerunValidation,
} from '@/services/templateWorkflowActions';
import { cn } from '@/lib/utils';

interface TemplateWorkflowTimelineProps {
  workflowState: TemplateWorkflowState;
  conversationId: string;
  className?: string;
}

// Step configurations with metadata
const TEMPLATE_STEP_CONFIGS = [
  {
    id: 'validation' as TemplateWorkflowStepId,
    title: 'Validation',
    description: 'Analyze uploaded resources and create asset inventory',
    stepNumber: 1,
  },
  {
    id: 'layout_generation' as TemplateWorkflowStepId,
    title: 'Layout Generation',
    description: 'Generate HTML template layouts with metadata',
    stepNumber: 2,
  },
  {
    id: 'design_document' as TemplateWorkflowStepId,
    title: 'Design Document',
    description: 'Create comprehensive design system documentation',
    stepNumber: 3,
  },
];

export function TemplateWorkflowTimeline({
  workflowState,
  conversationId,
  className,
}: TemplateWorkflowTimelineProps) {
  const {
    toggleTemplateStepExpanded,
    updateValidationStatus,
    updateLayoutGenerationStatus,
    updateDesignDocumentStatus,
  } = useTemplateWorkflowStore();

  // Auto-expand and navigate on step completion
  useEffect(() => {
    const { validation, layout_generation, design_document } = workflowState.steps;

    // When validation completes, auto-expand layout generation
    if (validation.status === 'completed' && layout_generation.status === 'pending' && !layout_generation.isExpanded) {
      console.log('🔽 [TemplateWorkflowTimeline] Auto-expanding layout_generation step');
      updateLayoutGenerationStatus(conversationId, { isExpanded: true });
    }

    // When layout generation completes, auto-expand design document
    if (layout_generation.status === 'completed' && design_document.status === 'pending' && !design_document.isExpanded) {
      console.log('🔽 [TemplateWorkflowTimeline] Auto-expanding design_document step');
      updateDesignDocumentStatus(conversationId, { isExpanded: true });
    }
  }, [
    conversationId,
    workflowState.steps,
    updateValidationStatus,
    updateLayoutGenerationStatus,
    updateDesignDocumentStatus,
  ]);

  // Generate step actions based on step state
  const getStepActions = (stepId: TemplateWorkflowStepId) => {
    const step = workflowState.steps[stepId];
    const actions = [];

    switch (stepId) {
      case 'validation':
        if (step.status === 'pending' || step.status === 'error') {
          actions.push({
            id: 'start_validation',
            label: step.status === 'error' ? 'Retry Validation' : 'Start Validation',
            variant: 'default' as const,
            isPrimary: true,
            onClick: () => startValidation(conversationId),
          });
        }
        if (step.status === 'completed') {
          actions.push({
            id: 'view_validation',
            label: 'View Results',
            variant: 'outline' as const,
            onClick: () => viewValidation(conversationId),
          });
          actions.push({
            id: 'rerun_validation',
            label: 'Rerun Validation',
            variant: 'outline' as const,
            onClick: () => rerunValidation(conversationId),
          });
        }
        break;

      case 'layout_generation':
        if (step.status === 'pending' && step.canProceed) {
          actions.push({
            id: 'start_layout_generation',
            label: 'Generate Layouts',
            variant: 'default' as const,
            isPrimary: true,
            onClick: () => startLayoutGeneration(conversationId),
          });
        }
        if (step.status === 'error' && step.canProceed) {
          actions.push({
            id: 'retry_layout_generation',
            label: 'Retry Generation',
            variant: 'default' as const,
            isPrimary: true,
            onClick: () => startLayoutGeneration(conversationId),
          });
        }
        if (step.status === 'completed') {
          actions.push({
            id: 'view_layouts',
            label: 'View Layouts',
            variant: 'outline' as const,
            onClick: () => viewLayouts(conversationId),
          });
        }
        break;

      case 'design_document':
        if (step.status === 'pending' && step.canProceed) {
          actions.push({
            id: 'start_design_document',
            label: 'Generate Document',
            variant: 'default' as const,
            isPrimary: true,
            onClick: () => startDesignDocument(conversationId),
          });
        }
        if (step.status === 'error' && step.canProceed) {
          actions.push({
            id: 'retry_design_document',
            label: 'Retry Generation',
            variant: 'default' as const,
            isPrimary: true,
            onClick: () => startDesignDocument(conversationId),
          });
        }
        if (step.status === 'completed') {
          actions.push({
            id: 'view_design_document',
            label: 'View Document',
            variant: 'outline' as const,
            onClick: () => viewDesignDocument(conversationId),
          });
        }
        break;
    }

    return actions;
  };

  return (
    <div className={cn('space-y-2', className)}>
      {TEMPLATE_STEP_CONFIGS.map((config, index) => {
        const stepStatus = workflowState.steps[config.id];
        const isLastStep = index === TEMPLATE_STEP_CONFIGS.length - 1;

        return (
          <WorkflowStep
            key={config.id}
            stepId={config.id}
            stepNumber={config.stepNumber}
            title={config.title}
            description={config.description}
            status={stepStatus}
            actions={getStepActions(config.id)}
            isExpanded={stepStatus.isExpanded || false}
            onToggleExpand={() => toggleTemplateStepExpanded(conversationId, config.id)}
            showConnector={!isLastStep}
            conversationId={conversationId}
          />
        );
      })}
    </div>
  );
}
